// Product Card Image Gallery Manager
// Prevent redeclaration if script is loaded multiple times
(function () {
  if (typeof window.ProductCardImageGallery !== "undefined") {
    return; // Class already defined, skip initialization
  }

  window.ProductCardImageGallery = class ProductCardImageGallery {
    constructor(cardElement) {
      this.card = cardElement;
      this.productId = cardElement.dataset.productId;
      this.imageSwiper = null;
      this.variantMap = new Map(); // Maps variant-id to slide index
      this.slideVariantMap = new Map(); // Maps slide index to variant-id
      this.generalImageIndices = []; // Tracks images without variant IDs (general product images)

      this.init();
    }

    init() {
      const imageSwiperEl = this.card.querySelector(".product-card-image-swiper");
      
      if (imageSwiperEl) {
        // Build variant mapping from slides (dynamic - only maps variants that have images)
        const slides = imageSwiperEl.querySelectorAll(".swiper-slide");
        this.generalImageIndices = []; // Track images without variant IDs

        slides.forEach((slide, index) => {
          const variantId = slide.dataset.variantId;
          if (variantId) {
            this.variantMap.set(variantId, index);
            this.slideVariantMap.set(index, variantId);
          } else {
            // Track images without variant IDs (general product images)
            this.generalImageIndices.push(index);
          }
        });

        // Initialize image swiper (inside each card)
        this.imageSwiper = new Swiper(imageSwiperEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          loop: false,
          centeredSlides: false,
          allowTouchMove: true,
          watchOverflow: true,
          normalizeSlideIndex: true,
          width: null,
          height: null,
          resistance: true,
          resistanceRatio: 0.5,
          preventInteractionOnTransition: true,
          touchEventsTarget: "container",
          navigation: {
            nextEl: imageSwiperEl.querySelector(".product-card-image-nav-next"),
            prevEl: imageSwiperEl.querySelector(".product-card-image-nav-prev"),
          },
          on: {
            slideChange: (swiper) => {
              this.handleImageSlideChange(swiper.activeIndex);
            },
            init: (swiper) => {
              this.handleImageSlideChange(swiper.activeIndex);
              setTimeout(() => {
                swiper.update();
                swiper.updateSlidesClasses();
              }, 50);
            },
            resize: (swiper) => {
              swiper.update();
            },
            touchStart: (swiper, event) => {
              event.stopPropagation();
              event.stopImmediatePropagation();
            },
            touchMove: (swiper, event) => {
              event.stopPropagation();
              event.stopImmediatePropagation();
            },
            touchEnd: (swiper, event) => {
              event.stopPropagation();
              event.stopImmediatePropagation();
            },
          },
        });

        // Click on image area → product page; swipe → carousel only (no navigation)
        const galleryEl = this.card.querySelector(".product-card-image-gallery");
        const productUrl = this.card.dataset.productUrl;
        if (galleryEl && productUrl) {
          let didSwipe = false;
          let pointerStart = null;
          const moveThreshold = 12;

          galleryEl.addEventListener("pointerdown", (e) => {
            pointerStart = { x: e.clientX, y: e.clientY };
            didSwipe = false;
          }, { passive: true });

          galleryEl.addEventListener("pointermove", (e) => {
            if (pointerStart != null) {
              const dx = e.clientX - pointerStart.x;
              const dy = e.clientY - pointerStart.y;
              if (Math.sqrt(dx * dx + dy * dy) > moveThreshold) didSwipe = true;
            }
          }, { passive: true });

          galleryEl.addEventListener("pointerup", () => { pointerStart = null; });
          galleryEl.addEventListener("pointercancel", () => { pointerStart = null; });

          galleryEl.addEventListener("click", (e) => {
            if (!didSwipe) {
              e.preventDefault();
              e.stopPropagation();
              window.location.href = productUrl;
            }
          });
        }
      }

      // Always set up color pickers and add to cart, even if no image swiper
      this.setupColorPickers();
      this.setupAddToCart();
    }

    setupAddToCart() {
      const form = this.card.querySelector("form.product-card-form");
      if (!form) return;

      const submitButton = form.querySelector(".product-card-add-button");
      if (!submitButton) return;

      form.addEventListener("submit", async (e) => {
        e.preventDefault();

        // Don't proceed if button is disabled (sold out)
        if (submitButton.disabled) return;

        const buttonText = submitButton.querySelector(".product-card-add-button-text");
        if (!buttonText) return;

        // Get the cart drawer (same as curated-kit and product-form)
        const cartDrawer = document.querySelector("cart-notification") || document.querySelector("cart-drawer");

        // Disable button during submission (data-loading lets curated-kit keep this product in the total)
        const originalText = buttonText.textContent;
        submitButton.disabled = true;
        submitButton.setAttribute("data-loading", "true");
        buttonText.textContent = "Adding...";

        try {
          // Get variant ID from form
          const variantInput = form.querySelector('input[data-variant-id]');
          if (!variantInput || !variantInput.value) {
            throw new Error("Variant ID not found");
          }

          const variantId = parseInt(variantInput.value, 10);
          if (!variantId || isNaN(variantId)) {
            throw new Error("Invalid variant ID");
          }

          // Prepare fetch config (same as curated-kit)
          const config = typeof fetchConfig !== "undefined"
            ? fetchConfig("javascript")
            : {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/javascript",
                },
              };

          config.headers["X-Requested-With"] = "XMLHttpRequest";
          delete config.headers["Content-Type"]; // Required when using FormData

          const formData = new FormData(form);

          // Include sections if cart drawer exists (same as curated-kit)
          if (cartDrawer) {
            const sectionsToRender = cartDrawer.getSectionsToRender();
            const sectionIds = sectionsToRender.map((section) => section.id);
            formData.append("sections", sectionIds.join(","));
            formData.append("sections_url", window.location.pathname);
            cartDrawer.setActiveElement(document.activeElement);
          }

          config.body = formData;

          // Use cart_add_url like product-form does (this is /cart/add.js)
          const cartAddUrl = window.routes?.cart_add_url || "/cart/add.js";
          const response = await fetch(cartAddUrl, config);

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || errorData.message || "Failed to add item to cart");
          }

          const data = await response.json();

          // Check for errors in response (same as product-form)
          if (data.status) {
            throw new Error(data.description || "Failed to add item to cart");
          }

          // If no cart drawer, redirect to cart page (same as product-form)
          if (!cartDrawer) {
            window.location = window.routes?.cart_url || "/cart";
            return;
          }

          // Publish cart update event (same as product-form)
          if (typeof publish !== "undefined" && typeof PUB_SUB_EVENTS !== "undefined") {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: "product-card" });
          }

          // Update the cart drawer (same as product-form)
          cartDrawer.renderContents(data);

          // Explicitly open the cart drawer after updating
          setTimeout(() => {
            cartDrawer.open();
          }, 100);

          // Remove is-empty class if present (same as product-form)
          if (cartDrawer.classList.contains("is-empty")) {
            cartDrawer.classList.remove("is-empty");
          }

          // Show success message
          buttonText.textContent = "Added to cart";
          setTimeout(() => {
            buttonText.textContent = originalText;
            submitButton.disabled = false;
            submitButton.removeAttribute("data-loading");
          }, 2000);
        } catch (error) {
          console.error("Error adding to cart:", error);
          buttonText.textContent = originalText;
          submitButton.disabled = false;
          submitButton.removeAttribute("data-loading");
          // Optionally show error message to user
          alert(error.message || "Failed to add item to cart. Please try again.");
        }
      });
    }

    setupColorPickers() {
      const colorOptions = this.card.querySelectorAll(".product-card-color-option");
      colorOptions.forEach((colorOption) => {
        const isOutOfStock =
          colorOption.dataset.available === "false" ||
          colorOption.classList.contains("out-of-stock");

        if (isOutOfStock) {
          colorOption.style.cursor = "not-allowed";
        } else {
          colorOption.style.pointerEvents = "auto";
          colorOption.style.cursor = "pointer";
        }

        colorOption.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          const variantId = colorOption.dataset.variantId;
          this.selectVariant(variantId, colorOption);
        });
      });
    }

    updatePrice(variantPrice, variantComparePrice) {
      if (!variantPrice) return;

      const priceContainer = this.card.querySelector(".product-card-price");
      const priceSale = priceContainer
        ? priceContainer.querySelector(".product-card-price-sale")
        : null;
      const priceOriginal = priceContainer
        ? priceContainer.querySelector(".product-card-price-original")
        : null;

      if (priceSale) {
        // Format price using Shopify's formatMoney function if available
        const formatMoneyValue = (cents) => {
          if (typeof window.formatMoney === 'function') {
            return window.formatMoney(cents);
          }
          // Fallback: format manually
          const dollars = (parseInt(cents) / 100).toFixed(2);
          return `$${parseFloat(dollars).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        };

        const formattedPrice = formatMoneyValue(variantPrice);
        priceSale.textContent = formattedPrice;
        priceSale.setAttribute("data-price", variantPrice);

        // Update compare at price if it exists
        if (variantComparePrice && parseInt(variantComparePrice) > parseInt(variantPrice)) {
          const formattedComparePrice = formatMoneyValue(variantComparePrice);

          if (priceOriginal) {
            priceOriginal.textContent = formattedComparePrice;
            priceOriginal.style.display = "";
          } else if (priceContainer) {
            // Create original price element if it doesn't exist
            const newPriceOriginal = document.createElement("span");
            newPriceOriginal.className = "product-card-price-original";
            newPriceOriginal.setAttribute("aria-label", "Original price");
            newPriceOriginal.textContent = formattedComparePrice;
            priceContainer.insertBefore(newPriceOriginal, priceSale);
          }
        } else {
          // Hide original price if compare price doesn't exist or isn't greater
          if (priceOriginal) {
            priceOriginal.style.display = "none";
          }
        }
      }
    }

    selectVariant(variantId, clickedElement) {
      if (!clickedElement || !this.imageSwiper) return;

      const isOutOfStock =
        clickedElement.dataset.available === "false" ||
        clickedElement.classList.contains("out-of-stock");

      // Update active state on all color pickers
      const colorOptions = this.card.querySelectorAll(".product-card-color-option");
      colorOptions.forEach((option) => option.classList.remove("active"));
      clickedElement.classList.add("active");

      // Update form variant ID if form exists
      const form = this.card.querySelector("form");
      const variantInput = form ? form.querySelector('input[data-variant-id]') : null;
      if (variantInput && variantId) {
        variantInput.value = variantId;
      }

      // Update add to cart button state based on variant availability
      const addToCartButton = form ? form.querySelector(".product-card-add-button") : null;
      const buttonText = addToCartButton
        ? addToCartButton.querySelector(".product-card-add-button-text")
        : null;

      if (addToCartButton && buttonText) {
        if (isOutOfStock) {
          addToCartButton.disabled = true;
          buttonText.textContent = "SOLD OUT";
        } else {
          addToCartButton.disabled = false;
          buttonText.textContent = "ADD TO CART";
        }
      }

      // Update price if variant has different price
      const variantPrice = clickedElement.dataset.variantPrice;
      const variantComparePrice = clickedElement.dataset.variantComparePrice;
      this.updatePrice(variantPrice, variantComparePrice);

      // Navigate to variant image if it exists
      if (variantId && this.variantMap.has(variantId)) {
        const slideIndex = this.variantMap.get(variantId);
        if (slideIndex !== undefined && slideIndex !== null) {
          this.imageSwiper.slideTo(slideIndex);
          return;
        }
      }

      // Variant doesn't have an associated image - find a general image to show
      if (this.generalImageIndices.length === 0) return;

      const currentIndex = this.imageSwiper.activeIndex;
      const currentSlideVariantId = this.slideVariantMap.get(currentIndex);

      if (currentSlideVariantId) {
        let nextGeneralIndex = this.generalImageIndices.find((idx) => idx > currentIndex);
        if (nextGeneralIndex === undefined) {
          nextGeneralIndex = this.generalImageIndices[0];
        }
        this.imageSwiper.slideTo(nextGeneralIndex);
      }
    }

    handleImageSlideChange(activeIndex) {
      const variantId = this.slideVariantMap.get(activeIndex);

      if (variantId) {
        const colorOption = this.card.querySelector(
          `.product-card-color-option[data-variant-id="${variantId}"]`
        );
        if (colorOption) {
          const colorOptions = this.card.querySelectorAll(".product-card-color-option");
          colorOptions.forEach((option) => option.classList.remove("active"));
          colorOption.classList.add("active");

          // Update price if variant has different price
          const variantPrice = colorOption.dataset.variantPrice;
          const variantComparePrice = colorOption.dataset.variantComparePrice;
          this.updatePrice(variantPrice, variantComparePrice);
        }
      }
    }

    addImage(imageUrl, altText, variantId = null) {
      if (!this.imageSwiper) return;

      const slideHtml = `
        <div class="swiper-slide" ${variantId ? `data-variant-id="${variantId}"` : ""}>
          <img class="product-card-image" src="${imageUrl}" alt="${altText}" />
        </div>
      `;

      const wrapper = this.imageSwiper.wrapperEl;
      wrapper.insertAdjacentHTML("beforeend", slideHtml);

      if (variantId) {
        const newIndex = this.imageSwiper.slides.length;
        this.variantMap.set(variantId, newIndex);
        this.slideVariantMap.set(newIndex, variantId);
      }

      this.imageSwiper.update();
    }
  };
})();

// Use the class from window for consistency (only if not already defined in this scope)
if (typeof ProductCardImageGallery === "undefined") {
  var ProductCardImageGallery = window.ProductCardImageGallery;
}

// Prevent card link navigation when clicking on interactive elements (scoped to section)
function setupCardLinkPrevention(sectionElement) {
  const cards = sectionElement
    ? sectionElement.querySelectorAll(".product-card")
    : document.querySelectorAll(".product-cards-section .product-card");

  cards.forEach((card) => {
    const interactiveElements = card.querySelectorAll(
      ".product-card-image-gallery, .product-card-colors, .product-card-add-button, .product-card-form"
    );

    interactiveElements.forEach((element) => {
      element.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    });
  });
}

// Track initialized sections to avoid double-initialization (use window so script can load multiple times)
if (typeof window.__eeCarouselInitializedSections === "undefined") {
  window.__eeCarouselInitializedSections = new Set();
}
var initializedSections = window.__eeCarouselInitializedSections;

// Initialize a single carousel section
function initializeCarouselSection(section) {
  // Check if already initialized
  if (section.dataset.carouselInitialized === 'true') {
    return;
  }

  // Extract section ID from class
  const sectionClasses = section.className.split(" ");
  let sectionId = null;
  let isCuratedKit = false;
  
  for (let cls of sectionClasses) {
    if (cls.startsWith("product-cards-section--")) {
      sectionId = cls.replace("product-cards-section--", "");
      break;
    } else if (cls.startsWith("curated-kit-section--")) {
      sectionId = cls.replace("curated-kit-section--", "");
      isCuratedKit = true;
      break;
    }
  }
  if (!sectionId) return;

  // Only initialize outer swiper for carousel sections
  const swiperEl = section.querySelector(".product-cards-swiper");
  if (!swiperEl && !isCuratedKit) return;

  setupCardLinkPrevention(section);

  // Initialize product card image galleries (works for both carousel and curated kit)
  // Also initializes add-to-cart functionality for all cards
  const productCards = section.querySelectorAll(".product-card[data-product-id]");
  const imageGalleries = [];

  productCards.forEach((card) => {
    // Skip if already initialized
    if (card.dataset.galleryInitialized === 'true') return;
    try {
      const gallery = new ProductCardImageGallery(card);
      imageGalleries.push(gallery);
      card.dataset.galleryInitialized = 'true';
    } catch (e) {
      console.warn('Failed to initialize product card gallery:', e);
    }
  });

  if (!window.productCardImageGalleries) {
    window.productCardImageGalleries = {};
  }
  window.productCardImageGalleries[sectionId] = imageGalleries;

  // Only initialize outer swiper for carousel sections (not curated kit)
  if (!swiperEl || isCuratedKit) {
    section.dataset.carouselInitialized = 'true';
    return;
  }

  // ✅ Much more reliable than manual width math (and works with offsets/spacings)
  function updateArrowVisibility(swiperInstance, sectionElement) {
    const swiperContainer = sectionElement.querySelector(".product-cards-swiper-container");
    const prevButton = swiperContainer
      ? swiperContainer.querySelector(".product-cards-swiper-button-prev")
      : null;
    const nextButton = swiperContainer
      ? swiperContainer.querySelector(".product-cards-swiper-button-next")
      : null;

    if (!prevButton || !nextButton) return;

    // If watchOverflow + no overflow => swiper locks
    if (swiperInstance.isLocked) {
      prevButton.classList.add("is-hidden");
      nextButton.classList.add("is-hidden");
      // Add class to swiper element when locked (all cards visible)
      swiperInstance.el.classList.add("swiper-locked");
      // When locked, reset transform to ensure CSS justify-content centering works correctly
      // Use requestAnimationFrame to ensure CSS has been applied
      requestAnimationFrame(() => {
        const wrapper = swiperInstance.wrapperEl;
        if (wrapper) {
          // Reset any transforms that Swiper may have applied
          wrapper.style.transform = 'translate3d(0px, 0, 0)';
        }
        swiperInstance.update();
      });
      return;
    }

    // Remove locked class when swiper is active (swipable)
    swiperInstance.el.classList.remove("swiper-locked");
    const epsilon = 0.005;
    const progress = typeof swiperInstance.progress === "number" ? swiperInstance.progress : 0;
    const isAtStart = progress <= epsilon;
    const isAtEnd = progress >= 1 - epsilon;
    prevButton.classList.toggle("is-hidden", isAtStart);
    nextButton.classList.toggle("is-hidden", isAtEnd);
  }

  // ✅ Outer swiper: fixed-width cards + consistent snap + mobile offset handled by Swiper
  const swiper = new Swiper(swiperEl, {
    slidesPerView: "auto",
    spaceBetween: 20,          // spacing controlled by Swiper (NOT CSS margin-right)
    freeMode: false,
    centeredSlides: false,
    resistance: true,
    resistanceRatio: 0.5,
    watchOverflow: true,
    preventInteractionOnTransition: true,
    observer: true,
    observeParents: true,
    navigation: {
      nextEl: section.querySelector(".product-cards-swiper-button-next"),
      prevEl: section.querySelector(".product-cards-swiper-button-prev"),
    },
    breakpoints: {
      // Mobile (matches your old visual padding-left:20, but correctly included in snapping)
      0: {
        slidesPerView: "auto",
        spaceBetween: 20,
        slidesOffsetBefore: 20,
        slidesOffsetAfter: 20,
      },
      // Larger mobile / tablet
      481: {
        slidesPerView: "auto",
        spaceBetween: 20,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      // Desktop
      768: {
        slidesPerView: "auto",
        spaceBetween: 20,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
    },
    on: {
      init: function () {
        // Make sure Swiper calculates AFTER images/fonts settle
        // Use requestAnimationFrame to ensure CSS layout (including justify-content) has been applied
        requestAnimationFrame(() => {
          this.update();
          updateArrowVisibility(this, section);
          // If swiper is locked with center alignment, ensure we're at the correct position
          if (this.isLocked && this.el.classList.contains("swiper-align-center")) {
            this.slideTo(0, 0);
          }
        });
      },
      resize: function () {
        this.update();
        updateArrowVisibility(this, section);
      },
      setTranslate: function () {
        updateArrowVisibility(this, section);
      },
      transitionEnd: function () {
        updateArrowVisibility(this, section);
      },
      slideChange: function () {
        updateArrowVisibility(this, section);
      },
      breakpoint: function () {
        this.update();
        updateArrowVisibility(this, section);
      },
      imagesReady: function () {
        this.update();
        updateArrowVisibility(this, section);
      },
    },
  });

  // Also update on window resize (scoped to this section's swiper)
  let resizeTimer;
  const resizeHandler = function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      swiper.update();
      updateArrowVisibility(swiper, section);
    }, 250);
  };
  window.addEventListener("resize", resizeHandler);

  // Mark as initialized
  section.dataset.carouselInitialized = 'true';
  initializedSections.add(sectionId);
}

// Initialize all carousel sections
function initializeAllCarousels() {
  // Handle both product-cards-section (carousel) and curated-kit-section
  const carouselSections = document.querySelectorAll(
    '.product-cards-section[class*="product-cards-section--"]'
  );
  const curatedKitSections = document.querySelectorAll(
    '.curated-kit-section[class*="curated-kit-section--"]'
  );
  const allSections = [...carouselSections, ...curatedKitSections];

  allSections.forEach((section) => {
    initializeCarouselSection(section);
  });
}

document.addEventListener("DOMContentLoaded", function () {
  initializeAllCarousels();

  // Watch for dynamically loaded content in product-recommendations elements
  const productRecommendationsElements = document.querySelectorAll('product-recommendations');
  
  productRecommendationsElements.forEach((element) => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain carousel sections
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) { // Element node
              // Look for carousel sections in the added content
              const carouselSections = node.querySelectorAll?.('.product-cards-section[class*="product-cards-section--"]') || [];
              carouselSections.forEach((section) => {
                // Small delay to ensure DOM is fully ready
                setTimeout(() => {
                  initializeCarouselSection(section);
                }, 100);
              });
              
              // Also check if the node itself is a carousel section
              if (node.classList?.contains('product-cards-section') && node.className.includes('product-cards-section--')) {
                setTimeout(() => {
                  initializeCarouselSection(node);
                }, 100);
              }
            }
          });
        }
      });
    });

    // Start observing
    observer.observe(element, {
      childList: true,
      subtree: true
    });
  });
});